import { Particle } from '../types/algorithm';
import { calculateParticleFitness } from '../utils/fitnessCalculator';

/**
 * Particle Swarm Optimization (PSO) implementation from scratch
 * Based on swarm intelligence with velocity and position updates
 */

/**
 * Initializes particle swarm
 */
export function initializeSwarm(
  swarmSize: number,
  taskCount: number,
  serviceCount: number
): Particle[] {
  const swarm: Particle[] = [];

  for (let i = 0; i < swarmSize; i++) {
    const particle: Particle = {
      position: new Array(taskCount),
      velocity: new Array(taskCount).fill(0),
      personalBest: new Array(taskCount),
      fitness: 0,
      personalBestFitness: 0,
      rwp: 0,
    };

    // Random initialization
    for (let j = 0; j < taskCount; j++) {
      particle.position[j] = Math.floor(Math.random() * serviceCount);
    }

    particle.personalBest = [...particle.position];
    swarm.push(particle);
  }

  return swarm;
}

/**
 * Updates particle velocity and position (discrete PSO)
 */
export function updateParticle(
  particle: Particle,
  globalBest: Particle,
  taskCount: number,
  serviceCount: number,
  w: number, // inertia weight
  c1: number, // cognitive parameter
  c2: number  // social parameter
): void {
  // For discrete PSO, we use probability-based position updates
  // Velocity magnitude determines probability of following best solutions

  for (let i = 0; i < taskCount; i++) {
    const r1 = Math.random();
    const r2 = Math.random();

    // Update velocity (conceptual, used to determine movement probability)
    const cognitive = c1 * r1 * (particle.personalBest[i] - particle.position[i]);
    const social = c2 * r2 * (globalBest.position[i] - particle.position[i]);
    const velocityComponent = w * particle.velocity[i] + cognitive + social;

    // Clamp velocity
    const maxVelocity = 5.0;
    particle.velocity[i] = Math.max(-maxVelocity, Math.min(maxVelocity, velocityComponent));

    // Update position based on velocity probability
    const followProbability = Math.abs(particle.velocity[i]) / maxVelocity;
    const r = Math.random();

    if (r < followProbability * 0.5) {
      // Follow personal best
      particle.position[i] = particle.personalBest[i];
    } else if (r < followProbability) {
      // Follow global best
      particle.position[i] = globalBest.position[i];
    } else {
      // Random exploration
      particle.position[i] = Math.floor(Math.random() * serviceCount);
    }
  }
}

/**
 * Finds global best particle
 */
export function findGlobalBest(swarm: Particle[]): Particle {
  let best = swarm[0];
  for (const particle of swarm) {
    if (particle.fitness > best.fitness) {
      best = particle;
    }
  }
  return best;
}

/**
 * PSO evolution for one generation
 */
function evolvePSO(
  swarm: Particle[],
  globalBest: Particle,
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  generation: number,
  maxGenerations: number
): void {
  // Linearly decreasing inertia weight: 0.9 -> 0.4
  const wMax = 0.9;
  const wMin = 0.4;
  const w = wMax - ((wMax - wMin) * generation) / maxGenerations;

  const c1 = 2.0; // cognitive parameter
  const c2 = 2.0; // social parameter

  for (const particle of swarm) {
    // Update velocity and position
    updateParticle(particle, globalBest, taskCount, serviceCount, w, c1, c2);

    // Evaluate fitness and update personal best
    calculateParticleFitness(particle, matrix, taskCount, serviceCount);
  }
}

/**
 * Runs PSO for specified generations
 */
export async function runPSO(
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  parameters: {
    populationSize: number;
    generations: number;
  },
  onGenerationComplete?: (generation: number, bestFitness: number) => void
): Promise<Particle> {
  // Initialize swarm
  let swarm = initializeSwarm(parameters.populationSize, taskCount, serviceCount);

  // Evaluate initial swarm
  for (const particle of swarm) {
    calculateParticleFitness(particle, matrix, taskCount, serviceCount);
  }

  let globalBest = findGlobalBest(swarm);

  // Evolution loop
  for (let gen = 0; gen < parameters.generations; gen++) {
    evolvePSO(swarm, globalBest, taskCount, serviceCount, matrix, gen, parameters.generations);
    globalBest = findGlobalBest(swarm);

    // Report progress
    if (onGenerationComplete) {
      onGenerationComplete(gen + 1, globalBest.fitness);
    }

    // Yield control to allow UI updates
    await new Promise(resolve => setTimeout(resolve, 0));
  }

  return globalBest;
}

